import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CommonModule, DatePipe, DecimalPipe } from "@angular/common";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { SharedModule } from "../../../../shared/shared.module";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { InputSearchComponent } from "../../../../shared/modules/input-search/input-search.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { EtatOfComponent } from "../../../of/components/etat-of/etat-of.component";
import { OF } from "../../../of/models/of.model";
import { OfEtat } from "../../../of/enums/etat-of.enum";
import { WidgetSubType } from "../../models/dashboard.model";
import { exportOfListExcel } from "./of-list-excel.utils";

export interface OfListDialogData {
  ofs: OF[];
  initialSubType?: WidgetSubType;
}

type SortKey =
  | "numero"
  | "ligne"
  | "article"
  | "quantite"
  | "etat"
  | "dateDebut"
  | "attente";

interface SubTypeOption {
  name: string;
  value: WidgetSubType | "ALL";
  etat: OfEtat | null;
}

const SUB_TYPE_TO_ETAT: Record<WidgetSubType, OfEtat | null> = {
  [WidgetSubType.OF_ATTENTE_PLANIF]: OfEtat.EN_ATTENTE_PLANNIF,
  [WidgetSubType.OF_ATTENTE_PROD]: OfEtat.EN_ATTENTE_PROD,
  [WidgetSubType.OF_EN_PROD]: OfEtat.EN_PROD,
  [WidgetSubType.OF_ATTENTE_REINTEGRATION]: OfEtat.EN_ATTENTE_REINTE_PDC,
  [WidgetSubType.OF_ATTENTE_VALIDATION]: OfEtat.EN_ATTENTE_VALIDATION,
  // Legacy
  [WidgetSubType.OF_ATTENTE_BILAN_MATIERE]: OfEtat.EN_ATTENTE_BILAN_MATIERE,
} as Record<WidgetSubType, OfEtat | null>;

@Component({
  selector: "vl-of-list-dialog",
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    DecimalPipe,
    SharedModule,
    DialogHeaderExportComponent,
    ChipsComponent,
    InputSearchComponent,
    RadioCardComponent,
    EtatOfComponent,
    MatIcon,
    MatTooltip,
  ],
  templateUrl: "./of-list-dialog.component.html",
  styleUrl: "./of-list-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OfListDialogComponent {
  data = inject<OfListDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<OfListDialogComponent, OF | null>);

  exporting = signal(false);
  search = signal<string>("");
  sortKey = signal<SortKey>("dateDebut");
  sortDesc = signal<boolean>(false);
  subType = signal<WidgetSubType | "ALL">(this.data.initialSubType ?? "ALL");

  /** Sous-types disponibles + comptes (visibles dans les chips). */
  subTypeOptions = computed<(SubTypeOption & { count: number })[]>(() => {
    const ofs = this.data.ofs ?? [];
    const opts: SubTypeOption[] = [
      { name: "Tous", value: "ALL", etat: null },
      {
        name: "Planification",
        value: WidgetSubType.OF_ATTENTE_PLANIF,
        etat: OfEtat.EN_ATTENTE_PLANNIF,
      },
      {
        name: "Production",
        value: WidgetSubType.OF_ATTENTE_PROD,
        etat: OfEtat.EN_ATTENTE_PROD,
      },
      {
        name: "En cours",
        value: WidgetSubType.OF_EN_PROD,
        etat: OfEtat.EN_PROD,
      },
      {
        name: "Réintégration",
        value: WidgetSubType.OF_ATTENTE_REINTEGRATION,
        etat: OfEtat.EN_ATTENTE_REINTE_PDC,
      },
      {
        name: "Validation",
        value: WidgetSubType.OF_ATTENTE_VALIDATION,
        etat: OfEtat.EN_ATTENTE_VALIDATION,
      },
    ];
    return opts.map((o) => ({
      ...o,
      count: o.etat == null ? ofs.length : ofs.filter((x) => x.etat === o.etat).length,
    }));
  });

  /** Liste des OFs filtrés par sub-type + recherche, puis triée. */
  filteredOfs = computed<OF[]>(() => {
    const ofs = this.data.ofs ?? [];
    const sub = this.subType();
    const q = this.search().trim().toLowerCase();

    let list = ofs;
    if (sub !== "ALL") {
      const etat = SUB_TYPE_TO_ETAT[sub];
      if (etat != null) list = list.filter((o) => o.etat === etat);
    }
    if (q) {
      list = list.filter((o) => {
        const numero = o.numero?.toLowerCase() ?? "";
        const ligne = o.ligne?.denomination?.toLowerCase() ?? "";
        const article =
          (o.articleInfos?.denomination ?? "").toLowerCase() +
          " " +
          (o.articleInfos?.code ?? "").toLowerCase();
        return numero.includes(q) || ligne.includes(q) || article.includes(q);
      });
    }
    return this.sortList(list);
  });

  stats = computed(() => {
    const ofs = this.filteredOfs();
    const total = ofs.length;
    const ages = ofs
      .map((o) => this.attenteJours(o))
      .filter((d): d is number => d != null);
    const oldest =
      ages.length > 0 ? Math.max(...ages) : null;
    const avg =
      ages.length > 0 ? ages.reduce((s, v) => s + v, 0) / ages.length : null;

    const byEtat = new Map<OfEtat, number>();
    for (const o of ofs) {
      byEtat.set(o.etat, (byEtat.get(o.etat) ?? 0) + 1);
    }
    let topEtatLabel = "—";
    let topEtatCount = 0;
    for (const [etat, count] of byEtat.entries()) {
      if (count > topEtatCount) {
        topEtatCount = count;
        topEtatLabel = this.etatLabel(etat);
      }
    }

    return {
      total,
      oldest,
      avg,
      topEtatLabel,
      topEtatCount,
    };
  });

  /** Date de référence : prévue (`dateDebut`) sinon date de création de l'OF. */
  refDate(o: OF): Date | null {
    const raw = o.dateDebut ?? (o as any).createDateTime ?? null;
    if (!raw) return null;
    const d = new Date(raw as any);
    return isNaN(d.getTime()) ? null : d;
  }

  /** Indique si la date affichée est la dateDebut prévue ou la création. */
  isPlannedDate(o: OF): boolean {
    return !!o.dateDebut;
  }

  attenteJours(o: OF): number | null {
    const ref = this.refDate(o);
    if (!ref) return null;
    const ms = Date.now() - ref.getTime();
    if (ms < 0) return 0;
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  }

  toggleSort(key: SortKey): void {
    if (this.sortKey() === key) {
      this.sortDesc.set(!this.sortDesc());
    } else {
      this.sortKey.set(key);
      this.sortDesc.set(key === "dateDebut" || key === "attente" || key === "quantite");
    }
  }

  private sortList(list: OF[]): OF[] {
    const key = this.sortKey();
    const desc = this.sortDesc();
    const mult = desc ? -1 : 1;
    const arr = [...list];
    arr.sort((a, b) => {
      switch (key) {
        case "numero":
          return (a.numero ?? "").localeCompare(b.numero ?? "") * mult;
        case "ligne":
          return (
            (a.ligne?.denomination ?? "").localeCompare(b.ligne?.denomination ?? "") * mult
          );
        case "article":
          return (
            (a.articleInfos?.denomination ?? "").localeCompare(
              b.articleInfos?.denomination ?? "",
            ) * mult
          );
        case "quantite":
          return ((a.quantite ?? 0) - (b.quantite ?? 0)) * mult;
        case "etat":
          return (a.etat ?? "").localeCompare(b.etat ?? "") * mult;
        case "dateDebut": {
          const ta = a.dateDebut ? new Date(a.dateDebut as any).getTime() : 0;
          const tb = b.dateDebut ? new Date(b.dateDebut as any).getTime() : 0;
          return (ta - tb) * mult;
        }
        case "attente": {
          const aa = this.attenteJours(a) ?? -1;
          const ab = this.attenteJours(b) ?? -1;
          return (aa - ab) * mult;
        }
      }
    });
    return arr;
  }

  etatLabel(etat: OfEtat): string {
    switch (etat) {
      case OfEtat.EN_ATTENTE_PLANNIF:
        return "Planification";
      case OfEtat.EN_ATTENTE_PROD:
        return "Production";
      case OfEtat.EN_PROD:
        return "En cours";
      case OfEtat.EN_ATTENTE_REINTE_PDC:
        return "Réintégration";
      case OfEtat.EN_ATTENTE_VALIDATION:
        return "Validation";
      case OfEtat.EN_ATTENTE_BILAN_MATIERE:
        return "Bilan matière";
      default:
        return etat;
    }
  }

  onSelectOf(of: OF): void {
    this.dialogRef.close(of);
  }

  async exportExcel(): Promise<void> {
    this.exporting.set(true);
    try {
      await exportOfListExcel({
        ofs: this.filteredOfs(),
        allOfs: this.data.ofs,
        subTypeLabel:
          this.subType() === "ALL"
            ? "Tous les états"
            : this.subTypeOptions().find((o) => o.value === this.subType())
                ?.name ?? "Tous les états",
        stats: this.stats(),
        attenteJours: (o) => this.attenteJours(o),
        etatLabel: (e) => this.etatLabel(e),
      });
    } finally {
      this.exporting.set(false);
    }
  }
}
